import { Edit3, Trash2 } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { getInitials, resolveImageUrlCandidates } from "../../utils/googleDriveImage";

function TestimonialAvatar({ name, studentPhoto }) {
  const [candidateIndex, setCandidateIndex] = useState(0);
  const imageCandidates = useMemo(
    () => resolveImageUrlCandidates(studentPhoto),
    [studentPhoto]
  );
  const imageUrl = imageCandidates[candidateIndex] || "";
  const initials = getInitials(name);

  useEffect(() => {
    setCandidateIndex(0);
  }, [studentPhoto]);

  const handleImageError = () => {
    setCandidateIndex((current) => {
      if (current + 1 < imageCandidates.length) {
        return current + 1;
      }
      return imageCandidates.length;
    });
  };

  if (!imageUrl || candidateIndex >= imageCandidates.length) {
    return (
      <span className="inline-flex h-full w-full items-center justify-center text-sm font-semibold text-midnight-navy">
        {initials}
      </span>
    );
  }

  return (
    <img
      src={imageUrl}
      alt={name}
      className="h-full w-full object-cover"
      loading="lazy"
      onError={handleImageError}
    />
  );
}

function AdminTestimonialsList({ testimonials, onEdit, onDelete, isLoading = false }) {
  if (isLoading) {
    return (
      <section className="rounded-2xl border border-midnight-navy/12 bg-white/92 px-6 py-14 text-center shadow-soft">
        <p className="text-base font-semibold text-midnight-navy">Loading testimonials...</p>
      </section>
    );
  }

  if (!testimonials.length) {
    return (
      <section className="rounded-2xl border border-dashed border-midnight-navy/20 bg-white/85 px-6 py-14 text-center shadow-soft">
        <p className="text-xl font-semibold text-midnight-navy">No testimonials yet</p>
        <p className="mt-2 text-sm text-slate-text/75">
          Add your first testimonial using the form to start showing stories on the public page.
        </p>
      </section>
    );
  }

  return (
    <section className="rounded-2xl border border-midnight-navy/12 bg-white/92 p-6 shadow-soft">
      <h2 className="text-xl md:text-2xl">All Testimonials</h2>
      <p className="mt-1 text-sm text-slate-text/75">Latest testimonials appear first.</p>

      <div className="mt-6 space-y-4">
        {testimonials.map((item) => {
          const previewQuote =
            item.testimonial.length > 140 ? `${item.testimonial.slice(0, 140)}...` : item.testimonial;

          return (
            <article
              key={item.id}
              className="rounded-xl border border-midnight-navy/10 bg-white px-4 py-4 md:px-5"
            >
              <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
                <div className="flex items-start gap-3">
                  <div className="h-12 w-12 overflow-hidden rounded-full border border-midnight-navy/12 bg-sky-glow">
                    <TestimonialAvatar name={item.name} studentPhoto={item.studentPhoto} />
                  </div>

                  <div>
                    <p className="text-sm font-semibold text-midnight-navy">{item.name}</p>
                    <p className="text-xs uppercase tracking-[0.12em] text-royal-blue">
                      {item.country} · {item.course}
                    </p>
                    <p className="mt-2 text-sm text-slate-text/82">“{previewQuote}”</p>
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => onEdit(item)}
                    className="inline-flex items-center gap-1 rounded-lg border border-midnight-navy/15 px-3 py-2 text-xs font-semibold text-midnight-navy hover:border-royal-blue/35 hover:bg-royal-blue/5"
                  >
                    <Edit3 className="h-3.5 w-3.5" aria-hidden="true" />
                    Edit
                  </button>
                  <button
                    type="button"
                    onClick={() => onDelete(item)}
                    className="inline-flex items-center gap-1 rounded-lg border border-red-200 px-3 py-2 text-xs font-semibold text-red-600 hover:bg-red-50"
                  >
                    <Trash2 className="h-3.5 w-3.5" aria-hidden="true" />
                    Delete
                  </button>
                </div>
              </div>
            </article>
          );
        })}
      </div>
    </section>
  );
}

export default AdminTestimonialsList;
